import React, { memo, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Legend,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { themeColors } from '../../utils/theme';
import { useOutreachData } from '../../hooks/useOutreachData';
import { useTagManagement } from '../../hooks/useTagManagement';
import { OutreachCategory } from '../../types/outreach';

const COLORS = [themeColors.primary, '#9c27b0', '#2196f3', '#ff9800'];

const CampaignDistributionByTag: React.FC = () => {
  const theme = useTheme();
  const { campaigns, loading: campaignsLoading } = useOutreachData();
  const { tags: campaignTags, loading: campaignTagsLoading } = useTagManagement({
    category: OutreachCategory.CAMPAIGNS,
    includeGlobal: true,
  });

  const tagsById = useMemo(() => {
    const map = new Map<string, string>();
    campaignTags.forEach((tag) => {
      map.set(tag.id, tag.name);
    });
    return map;
  }, [campaignTags]);

  const { campaignDistribution, totalSavedCampaigns } = useMemo(() => {
    const savedCampaigns = campaigns.filter((campaign) => campaign.saved);
    const tagCounts = new Map<string, number>();

    const incrementTagCount = (tagName: string) => {
      const trimmedName = tagName.trim();
      if (!trimmedName) {
        return;
      }

      tagCounts.set(trimmedName, (tagCounts.get(trimmedName) ?? 0) + 1);
    };

    savedCampaigns.forEach((campaign) => {
      const resolvedTags = new Set<string>();

      if (campaign.tag) {
        if (typeof campaign.tag === 'string') {
          const mappedName = tagsById.get(campaign.tag) ?? campaign.tag;
          resolvedTags.add(mappedName);
        } else {
          if (campaign.tag.name) {
            resolvedTags.add(campaign.tag.name);
          } else if (campaign.tag.id) {
            const mappedName = tagsById.get(campaign.tag.id);
            if (mappedName) {
              resolvedTags.add(mappedName);
            }
          }
        }
      }

      if (Array.isArray(campaign.tags)) {
        campaign.tags.forEach((tag) => {
          if (tag) {
            const mappedName = tagsById.get(tag) ?? tag;
            resolvedTags.add(mappedName);
          }
        });
      }

      if (Array.isArray(campaign.tagIds)) {
        campaign.tagIds.forEach((tagId) => {
          if (tagId) {
            const mappedName = tagsById.get(tagId);
            if (mappedName) {
              resolvedTags.add(mappedName);
            }
          }
        });
      }

      if (campaign.tagId) {
        const mappedName = tagsById.get(campaign.tagId);
        if (mappedName) {
          resolvedTags.add(mappedName);
        }
      }

      if (resolvedTags.size === 0) {
        resolvedTags.add('Untagged');
      }

      resolvedTags.forEach((tagName) => {
        incrementTagCount(tagName);
      });
    });

    const distribution = Array.from(tagCounts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    return {
      campaignDistribution: distribution,
      totalSavedCampaigns: savedCampaigns.length,
    };
  }, [campaigns, tagsById]);

  const distributionLoading = campaignsLoading || campaignTagsLoading;

  const tooltipStyles = useMemo(
    () => ({
      content: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        padding: '8px',
        color: '#FFFFFF',
      },
      label: { color: '#FFFFFF' },
      item: { color: '#FFFFFF' },
    }),
    [theme]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: theme.palette.background.default,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Typography variant="subtitle1" gutterBottom>
        Outreach Campaign Distribution by Tag
      </Typography>
      <Box
        sx={{
          height: 250,
          mt: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {distributionLoading ? (
          <CircularProgress size={32} />
        ) : campaignDistribution.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No campaign data available.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={campaignDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                paddingAngle={5}
                dataKey="value"
                label={({ name, value }) => {
                  if (!totalSavedCampaigns) {
                    return `${name}: 0%`;
                  }
                  const percent = (value / totalSavedCampaigns) * 100;
                  return `${percent.toFixed(0)}%`;
                }}
              >
                {campaignDistribution.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ color: theme.palette.text.secondary }}
              />
              <Tooltip
                contentStyle={tooltipStyles.content}
                labelStyle={tooltipStyles.label}
                itemStyle={tooltipStyles.item}
                separator=""
                formatter={(value: number) => [
                  `${value} Campaign${value === 1 ? '' : 's'}`,
                  '',
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
};

export default memo(CampaignDistributionByTag);
